import { FC, ReactNode, useEffect } from 'react';
import { InjectIntlContext } from 'hooks/useTranslation';
import { IntlProvider } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import TagManager from 'react-gtm-module';
import { RootState } from 'store';
import { getLocalization } from 'api/localization';
import {
  addLocalizationDictionary,
  changeLocalizationLanguage,
} from 'actions/localization';
import DateHelpers from 'helpers/Date';
import LocalStorage from 'helpers/LocalStorage';
import { setAppTheme } from 'theme';
import Loader from 'components/ui/loader';

interface Props {
  children: ReactNode;
}

export const LocalizationProvider: FC<Props> = ({ children }) => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user);
  const interfaceLang = useSelector(
    (state: RootState) => state.localization.interfaceLang
  );
  const localeDictionaries = useSelector(
    (state: RootState) => state.localization.localeDictionaries
  );
  const languagesAvailable = useSelector(
    (state: RootState) => state.settings.languagesAvailable
  );
  const fontFamily = useSelector(
    (state: RootState) => state.settings.brandConfig.config.theme.fontFamily
  );

  const fetchLocalization = async () => {
    try {
      const { dictionaries } = await getLocalization();
      dispatch(addLocalizationDictionary(dictionaries));
    } catch (error) {
      console.error(error);
    }
  };

  const changeLocalization = async (interfaceLang: string) => {
    LocalStorage.set('noAuthLang', interfaceLang);

    if (!localeDictionaries[interfaceLang] && interfaceLang !== 'xx') {
      await fetchLocalization();
    }

    DateHelpers.setLocale(interfaceLang);
    TagManager.dataLayer({ dataLayer: { interfaceLang } });
    document.documentElement.setAttribute('lang', interfaceLang);
    setAppTheme({
      fontFamily: fontFamily[interfaceLang] ?? fontFamily.default,
    });
    dispatch(changeLocalizationLanguage(interfaceLang));
  };

  const initializeLocalization = async () => {
    let targetLang: string;

    if (user) {
      targetLang = user.interfaceLang;
    } else {
      const defaultLanguage = languagesAvailable[0]?.id;
      targetLang = LocalStorage.get('noAuthLang') || defaultLanguage;
    }

    await changeLocalization(targetLang);
  };

  useEffect(() => {
    initializeLocalization();
  }, []);

  useEffect(() => {
    if (
      user?.interfaceLang &&
      user?.interfaceLang !== LocalStorage.get('noAuthLang')
    ) {
      changeLocalization(user.interfaceLang);
    }
  }, [user]);

  if (!interfaceLang) {
    return (
      <div style={{ minHeight: '100vh' }}>
        <Loader />
      </div>
    );
  }

  return (
    <IntlProvider
      locale={interfaceLang}
      messages={localeDictionaries[interfaceLang]}
      onError={() => null}>
      <InjectIntlContext>{children}</InjectIntlContext>
    </IntlProvider>
  );
};
